import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Router } from '@angular/router';

import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { UserService } from './user.service';
import { OrganizationService } from './organization.service';
import { catchError, filter, switchMap, take } from "rxjs/operators";
import { RefreshAccessTokenResponse } from "@app/model";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
	private isRefreshing = false;
	private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

	constructor(private router: Router,
	            private location: Location,
	            private userService: UserService,
	            private organizationService: OrganizationService) {
	}

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		if (this.userService.jwtToken) {
			req = req.clone({
				headers: req.headers.append('Authorization', `Bearer ${this.userService.jwtToken}`)
			});
		}

		if (this.organizationService.currentOrganization.id) {
			req = req.clone({
				headers: req.headers.append('organization', this.organizationService.currentOrganization.alias)
			});
		}

		return next.handle(req).pipe(
			catchError((error: HttpErrorResponse) => {
				if (error.status === 401 && !this.isRefreshing && !req.url.includes('/refreshAccessToken')) {
					return this.handle401Error(req, next);
				}
				if(!this.isRefreshing && req.url.includes('/refreshAccessToken')) { // evitar loop infinito
					this.userService.logout();
				}

				return throwError(error);
			})
		);
	}

	private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
		if (!this.isRefreshing) {
			this.isRefreshing = true;
			this.refreshTokenSubject.next(null);

			return this.userService.refreshAccessToken().pipe(
				switchMap((response: RefreshAccessTokenResponse) => {
					this.isRefreshing = false;

					this.userService.jwtToken = response.accessToken;
					this.refreshTokenSubject.next(response.accessToken);
					localStorage.setItem('jwtToken', response.accessToken);

					return next.handle(this.addToken(request, response.accessToken));
				}),
				catchError((err) => {
					this.isRefreshing = false;
					this.userService.logout();
					return throwError(err);
				})
			);
		} else {
			return this.refreshTokenSubject.pipe(
				filter(token => token != null),
				take(1),
				switchMap(jwtToken => next.handle(this.addToken(request, jwtToken!)))
			);
		}
	}

	private addToken(request: HttpRequest<any>, token: string) {
		let headers = request.headers.set('Authorization', `Bearer ${token}`);

		if (this.organizationService.currentOrganization.id) {
			headers = headers.set('organization', this.organizationService.currentOrganization.alias);
		}

		return request.clone({ headers });
	}

}
